// =============================================================================
// Origin UI — src/pages/admin/HelpKnowledgeBaseAdminTab.tsx
// Admin tab for managing help assistant knowledge base documents.
// =============================================================================

import { useState, useEffect } from 'react';
import { helpAdminApi } from '@/services/helpAdminApi';
import type { KnowledgeBaseListItem, KnowledgeBaseDetail } from '@/services/helpAdminApi';
import { Card, ErrorBanner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';

export function HelpKnowledgeBaseAdminTab() {
  const [docs, setDocs] = useState<KnowledgeBaseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Editor state
  const [editing, setEditing] = useState<KnowledgeBaseDetail | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);

  async function loadDocs() {
    try {
      setLoading(true);
      const data = await helpAdminApi.listKnowledgeBase();
      setDocs(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDocs(); }, []);

  async function openDoc(id: string) {
    try {
      setError('');
      const doc = await helpAdminApi.getKnowledgeBase(id);
      setEditing(doc);
      setIsNew(false);
      setTitle(doc.title);
      setSortOrder(doc.sortOrder);
      setContent(doc.content);
      setIsActive(doc.isActive);
    } catch (e: any) {
      setError(e.message || 'Failed to load document');
    }
  }

  function startNew() {
    setEditing({} as KnowledgeBaseDetail);
    setIsNew(true);
    setTitle('');
    setSortOrder(docs.length);
    setContent('');
    setIsActive(true);
  }

  function cancelEdit() {
    setEditing(null);
    setIsNew(false);
  }

  async function save() {
    if (!title.trim()) { setError('Title is required'); return; }
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        await helpAdminApi.createKnowledgeBase({ title, sortOrder, content, isActive });
      } else if (editing) {
        await helpAdminApi.updateKnowledgeBase(editing.knowledgeBaseId, { title, sortOrder, content, isActive });
      }
      setEditing(null);
      setIsNew(false);
      await loadDocs();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      setError('');
      await helpAdminApi.deleteKnowledgeBase(deleteId);
      setDeleteId(null);
      if (editing?.knowledgeBaseId === deleteId) {
        setEditing(null);
        setIsNew(false);
      }
      await loadDocs();
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  }

  if (loading) return <div style={{ padding: 24, color: '#6B7280' }}>Loading knowledge base...</div>;

  // Editor view
  if (editing) {
    return (
      <div>
        {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h4 className={styles.formTitle}>{isNew ? 'New Document' : 'Edit Document'}</h4>
            {!isNew && (
              <button
                style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setDeleteId(editing.knowledgeBaseId)}
              >
                Delete
              </button>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Title</label>
            <input className={styles.fieldInput} value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title" />
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div className={styles.field} style={{ flex: '0 0 120px', marginBottom: 0 }}>
              <label className={styles.fieldLabel}>Sort Order</label>
              <input className={styles.fieldInput} type="number" value={sortOrder} onChange={e => setSortOrder(parseInt(e.target.value) || 0)} />
            </div>
            <div className={styles.field} style={{ flex: '0 0 120px', marginBottom: 0 }}>
              <label className={styles.fieldLabel}>Active</label>
              <div style={{ paddingTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                  {isActive ? 'Yes' : 'No'}
                </label>
              </div>
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Content (Markdown)</label>
            <textarea
              className={styles.fieldInput}
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={20}
              style={{
                fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                fontSize: 13,
                lineHeight: 1.6,
                resize: 'vertical',
                minHeight: 400,
              }}
              placeholder="Markdown content for this knowledge base document..."
            />
          </div>

          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </Card>

        <ConfirmDialog
          open={!!deleteId}
          title="Delete Document"
          message="Are you sure you want to delete this knowledge base document? This cannot be undone."
          onConfirm={confirmDelete}
          onCancel={() => setDeleteId(null)}
        />
      </div>
    );
  }

  // List view
  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 className={styles.formTitle}>Knowledge Base ({docs.length} documents)</h4>
          <button className={styles.saveBtn} onClick={startNew}>+ Add Document</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px', width: 60 }}>ORDER</th>
              <th style={{ padding: '8px 12px' }}>TITLE</th>
              <th style={{ padding: '8px 12px', width: 70 }}>ACTIVE</th>
              <th style={{ padding: '8px 12px' }}>LAST EDITED BY</th>
              <th style={{ padding: '8px 12px' }}>LAST EDITED AT</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => (
              <tr
                key={doc.knowledgeBaseId}
                style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                onClick={() => openDoc(doc.knowledgeBaseId)}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#6B7280' }}>{doc.sortOrder}</td>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{doc.title}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: doc.isActive ? '#F0FDF4' : '#FEF2F2',
                    color: doc.isActive ? '#22C55E' : '#DC2626',
                  }}>
                    {doc.isActive ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', color: '#6B7280' }}>{doc.lastEditedBy}</td>
                <td style={{ padding: '10px 12px', color: '#6B7280' }}>
                  {new Date(doc.lastEditedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
